import type { FC } from 'react';
import { useEffect, useState } from 'react';
import type { Resolver } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import {
  Alert,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader
} from '@cencosud-cencommerce/paris-uikit';
import { yupResolver } from '@hookform/resolvers/yup';

import { useAppSelector } from '@/Application/Hooks/Redux';
import {
  isDniFeatureEnabled,
  isDniRegisteredFeatureEnabled
} from '@/config/featureFlags';
import type { Address } from '@/modules/address/domain/Address';
import { SendShippingEvents } from '@/modules/shared/utils/gtmEvents';
import {
  ShippingEventInteractiveActions,
  ShippingEventInteractiveCategories
} from '@/modules/shared/utils/gtmEvents/types';
import { AddressForm } from '@/sections/address/components/AddressForm';
import { getFormDefaultValues } from '@/sections/address/components/AddressFormModal/utils';
import { addressFormToAddress } from '@/sections/address/mappers/addressFormToAddress';
import type { AddressFormValues } from '@/sections/address/types/AddressFormValues';
import { addressValidator } from '@/sections/address/validators/addressValidator';
import { useCustomerContext } from '@/sections/customer/CustomerContext';
import { usePlaceData } from '@/sections/region/GooglePlaceDataContext';
import useCommunesOptions from '@/sections/region/hooks/useCommunesOption';
import useRegions from '@/sections/region/hooks/useRegions';
import useRegionsOptions from '@/sections/region/hooks/useRegionsOptions';
import { LocalHookFormDevtool } from '@/sections/shared/hooks/local-hookform-devtool';

interface IOnSubmit {
  (address: Omit<Address, 'id'> | Address, saveAddress: boolean): void;
}

interface Props {
  'data-testid'?: string;
  isOpen: boolean;
  onClose: VoidFunction;
  showAlert: boolean;
  onSubmit: IOnSubmit;
  initialValues?: Address;
  isLoadingNewAddress?: boolean;
  onSaved?: VoidFunction;
}

const AddressFormModal: FC<Props> = ({
  isOpen,
  onClose,
  onSubmit,
  initialValues,
  showAlert,
  onSaved,
  'data-testid': dataTestId = 'paris-addressformmodal',
  isLoadingNewAddress = false
}) => {
  const cart = useAppSelector((state) => state.cart.data);
  const {
    customer: { data: customer }
  } = useCustomerContext();
  const { getRegionAndCommuneByIds } = useRegions();
  const isEdit = !!initialValues?.id;
  const modalTitle = isEdit ? 'Editar dirección' : 'Agregar dirección';

  const [selectedRegion, setSelectedRegion] = useState<number | null>(null);

  const isRegistered = customer !== null;
  const isDniEnabled = isRegistered
    ? isDniRegisteredFeatureEnabled()
    : isDniFeatureEnabled();
  const { placeData } = usePlaceData();
  const regionOptions = useRegionsOptions();
  const communeOptions = useCommunesOptions(selectedRegion ?? 0);

  const isGuest = !!cart?.anonymousId;
  const documentType = cart?.shippingAddress?.documentType;
  const defaultFormValues = getFormDefaultValues(isGuest, initialValues);

  const addressForm = useForm<AddressFormValues>({
    resolver: yupResolver(
      addressValidator(placeData, regionOptions, communeOptions, isGuest)
    ) as Resolver<AddressFormValues>,
    mode: 'onTouched',
    defaultValues: defaultFormValues
  });

  const { handleSubmit } = addressForm;

  const handleFormKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === 'Enter' &&
      !(document.activeElement as HTMLElement)?.dataset?.submitButton
    ) {
      e.preventDefault();
    }
  };

  const region = addressForm.watch('region');
  useEffect(() => {
    if (region) {
      setSelectedRegion(region);
    }
  }, [region]);

  const sendCloseAddressModalEvent = () => {
    const values = addressForm.getValues();
    const region = getRegionAndCommuneByIds(values.region, values.commune);

    SendShippingEvents.sendEvent({
      event: 'baseEvent',
      extraFields: {
        'event-interactive-accion':
          ShippingEventInteractiveActions.CloseAddressModal,
        'event-interactive-category':
          ShippingEventInteractiveCategories.Checkout,
        'event-interactive-tag': region
          ? `${region.name} / ${region.commune[0]?.name}`
          : 'sin información'
      }
    });
  };
  return (
    <FormProvider {...addressForm}>
      <form
        onSubmit={handleSubmit(async (addressForm) => {
          const region = getRegionAndCommuneByIds(
            addressForm.region,
            addressForm.commune
          );
          SendShippingEvents.sendEvent({
            event: 'baseEvent',
            extraFields: {
              'event-interactive-accion': addressForm.id
                ? ShippingEventInteractiveActions.UpdateAddress
                : ShippingEventInteractiveActions.SaveAddress,
              'event-interactive-tag': `${region?.name} / ${region?.commune[0]?.name}`
            }
          });
          try {
            await onSubmit(
              addressFormToAddress(addressForm),
              !isGuest && addressForm['save-address']
            );
            onSaved?.();
          } catch (e) {
            console.error(e);
          }
        })}
        onKeyDown={handleFormKeyDown}
      >
        <Modal isOpen={isOpen} data-testid={dataTestId} size="sm">
          <ModalHeader
            onClose={() => {
              sendCloseAddressModalEvent();
              onClose();
            }}
            icon={{ name: 'place', fill: 'info-500' }}
            title={modalTitle}
            titleVariant="body1"
          />
          <ModalBody className="co-gap-6 co-m-0 co-pl-1 co-pr-5 co-py-6">
            <div className="co-flex co-flex-col co-gap-6">
              {showAlert && (
                <Alert
                  title="Ingresa la dirección de entrega"
                  body="Completa tu dirección para continuar con el pago"
                  status="warning"
                />
              )}
              <AddressForm
                isEdit={isEdit}
                documentType={documentType}
                isDniEnabled={isDniEnabled}
              />
            </div>
          </ModalBody>
          <ModalFooter className="co-justify-end">
            <Button
              size="xl"
              className="co-w-full tablet:co-w-fit"
              type="submit"
              loading={isLoadingNewAddress}
              data-submit-button="true"
            >
              Guardar
            </Button>
          </ModalFooter>
        </Modal>
      </form>
      <LocalHookFormDevtool control={addressForm?.control} />
    </FormProvider>
  );
};

AddressFormModal.displayName = 'AddressFormModal';

export default AddressFormModal;
